import * as Sentry from '@sentry/capacitor';
import { init as initReactSentry, browserTracingIntegration } from '@sentry/react';
import type { SeverityLevel } from '@sentry/capacitor';
import { scrubSensitiveData } from './scrubSensitiveParams';
import type { UserProfile } from '@/api/user';

// Sentry replaces Firebase Crashlytics: @sentry/capacitor owns the native
// (iOS/Android) crash handlers while the sibling @sentry/react SDK captures the
// web/React layer — so both native crashes AND browser errors land in one
// project. Unhandled errors and promise rejections are captured automatically,
// on native and web alike.
const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// Events, transactions (name, request, spans) and breadcrumbs (navigation from/to, fetch) carry
// URLs that can hold the emailed reset/invite `token_hash`, an OAuth `code` or the stream's
// `access_token`.
export const scrubSentryPayload = <T>(payload: T): T => scrubSensitiveData(payload);

export const initErrorReporting = (): void => {
  if (!DSN) {
    // No DSN configured for this environment (e.g. local dev) — skip init so we
    // don't emit noise. Sentry.setUser/captureException are no-ops until init.
    return;
  }
  Sentry.init(
    {
      dsn: DSN,
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION as string | undefined,
      integrations: [browserTracingIntegration()],
      tracesSampleRate: 0.1,
      beforeSend: scrubSentryPayload,
      beforeSendTransaction: scrubSentryPayload,
      beforeBreadcrumb: scrubSentryPayload,
    },
    // Sibling init: wires the JS-layer SDK. Required for @sentry/capacitor.
    initReactSentry,
  );
};

export const identifySentryUser = (user: UserProfile | null): void => {
  if (!user) {
    return;
  }
  Sentry.setUser({ id: user.id });
};

export const resetSentryIdentity = (): void => {
  Sentry.setUser(null);
};

export const captureException = (error: Error): void => {
  Sentry.captureException(error);
};

// For expected-but-noteworthy conditions that are not crashes (e.g. a stream
// that fails to recover): report at a non-error level so they stay out of the
// error feed. Defaults to 'warning'.
export const captureMessage = (message: string, level: SeverityLevel = 'warning'): void => {
  Sentry.captureMessage(message, level);
};
